'use client'

import { useMemo, useState } from 'react'

import { ModelsNews } from '@/api/generated-api'
import { useNewsList } from '@/features/news/hooks'

import { NEWS_GRID_PAGE_SIZE, NEWS_LIST_FETCH_SIZE } from '../../constants'
import { useNewsHeroFeatured } from '../../hooks/useNewsHeroFeatured'
import type { NewsTagFilter } from '../../utils/newsTags'
import NewsListView from './NewsListView'

const NewsList = () => {
  const [selectedTag, setSelectedTag] = useState<NewsTagFilter>('all')

  const tag = selectedTag === 'all' ? undefined : selectedTag

  const { items: featuredItems, isLoading: isFeaturedLoading } =
    useNewsHeroFeatured()

  const {
    data: newsListData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading
  } = useNewsList({
    limit: NEWS_GRID_PAGE_SIZE,
    firstPageLimit:
      selectedTag === 'all' ? NEWS_LIST_FETCH_SIZE : NEWS_GRID_PAGE_SIZE,
    tag
  })

  const items = useMemo(() => {
    const pages = newsListData?.pages ?? []
    const news = pages.flatMap(page => page.news || []) as ModelsNews[]

    if (selectedTag !== 'all') {
      return news
    }

    // The hero is curated in the admin, so the articles to skip are whichever
    // ones it shows — not simply the newest four.
    const featuredIds = new Set(featuredItems.map(item => item.id))

    return news.filter(item => !featuredIds.has(item.id))
  }, [featuredItems, newsListData?.pages, selectedTag])

  return (
    <NewsListView
      items={items}
      selectedTag={selectedTag}
      onTagChange={setSelectedTag}
      onLoadMore={fetchNextPage}
      hasNextPage={Boolean(hasNextPage)}
      isFetchingNextPage={isFetchingNextPage}
      isLoading={isLoading || (selectedTag === 'all' && isFeaturedLoading)}
    />
  )
}

export default NewsList
